import { useState } from 'react';
import { authApi, twoFactorApi, ApiError, LoginData } from '@/services/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { AlertCircle, Loader2 } from 'lucide-react';

interface LoginFormProps {
  onLoginSuccess: (token: string, username: string) => void;
  onSwitchToSignup: () => void;
  onSwitchToConfirm: () => void;
}

export const LoginForm = ({ onLoginSuccess, onSwitchToSignup, onSwitchToConfirm }: LoginFormProps) => {
  const [loginData, setLoginData] = useState<LoginData>({
    username: '',
    password: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);

  // 2FA state variables
  const [requires2FA, setRequires2FA] = useState(false);
  const [tempToken, setTempToken] = useState<string>('');
  const [tempAccessToken, setTempAccessToken] = useState<string>('');
  const [tempRefreshToken, setTempRefreshToken] = useState<string>('');
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [trustDevice, setTrustDevice] = useState(false);
  const [twoFactorError, setTwoFactorError] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [attemptsRemaining, setAttemptsRemaining] = useState<number | null>(null);

  // Device token management
  const DEVICE_TOKEN_EXPIRY_DAYS = 30;

  const storeDeviceToken = (username: string, deviceToken: string) => {
    try {
      const normalizedUsername = username.toLowerCase();
      const expiryDate = new Date();
      expiryDate.setDate(expiryDate.getDate() + DEVICE_TOKEN_EXPIRY_DAYS);

      const tokenData = {
        token: deviceToken,
        expiry: expiryDate.toISOString(),
        username: normalizedUsername
      };

      const storageKey = `device_token_${normalizedUsername}`;
      localStorage.setItem(storageKey, JSON.stringify(tokenData));
      console.log('Device token stored successfully:', storageKey, 'expires:', expiryDate.toISOString());
    } catch (error) {
      // localStorage not available (e.g., incognito mode) - silently fail
      console.warn('Failed to store device token:', error);
    }
  };

  const getDeviceToken = (username: string): string | null => {
    try {
      const normalizedUsername = username.toLowerCase();
      const stored = localStorage.getItem(`device_token_${normalizedUsername}`);
      if (!stored) return null;

      const tokenData = JSON.parse(stored);
      const expiry = new Date(tokenData.expiry);

      // Check if expired
      if (new Date() > expiry) {
        localStorage.removeItem(`device_token_${normalizedUsername}`);
        return null;
      }

      return tokenData.token;
    } catch (error) {
      // localStorage not available (e.g., incognito mode) - silently fail
      console.warn('Failed to get device token:', error);
      return null;
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setLoginData((prev) => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!loginData.username || !loginData.password) {
      setError('Please enter username and password');
      return;
    }

    setIsLoading(true);

    try {
      // Check for stored device token (trusted device)
      const storedDeviceToken = getDeviceToken(loginData.username);

      const response = await authApi.login({
        ...loginData,
        device_token: storedDeviceToken || undefined
      } as any);

      // Check if 2FA is required
      if (response.requires_2fa) {
        setRequires2FA(true);
        setTempToken(response.temp_token || '');
        setTempAccessToken(response.temp_access_token || '');
        setTempRefreshToken(response.temp_refresh_token || '');
        setIsLoading(false);
        // Note: Verification code is automatically sent by the backend
        return;
      }

      if (response.id_token === 'none') {
        setError('Account suspended due to non-payment. Please contact support@patentloop.com');
        setIsLoading(false);
        return;
      }

      onLoginSuccess(response.id_token, response.username);
    } catch (err) {
      const error = err as ApiError;

      // Check for specific error types
      if (error.status === 402) {
        setError('Please complete payment with Stripe or contact support@patentloop.com');
      } else if (error.status === 403) {
        setError('Username or password incorrect');
      } else if (error.error && typeof error.error === 'string') {
        if (error.error.includes('UserNotConfirmedException')) {
          setError('Please check your email and confirm your account before logging in');
        } else if (error.error.includes('NotAuthorizedException') || error.error.includes('Incorrect username or password')) {
          setError('Username or password incorrect');
        } else {
          setError('Login failed. Please try again');
        }
      } else {
        setError(error.message || 'Login failed. Please try again');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Handle 2FA verification
  const handle2FAVerification = async (e: React.FormEvent) => {
    e.preventDefault();
    setTwoFactorError('');

    if (!twoFactorCode || twoFactorCode.length !== 6) {
      setTwoFactorError('Please enter a 6-digit verification code');
      return;
    }

    setIsVerifying(true);

    try {
      // First, verify the code (using login-specific endpoint that doesn't require auth)
      const verifyResult = await twoFactorApi.verify2FALoginCode(
        loginData.username,
        twoFactorCode,
        trustDevice,
        navigator.userAgent.slice(0, 50) // Device name
      );

      if (!verifyResult.success) {
        setTwoFactorError(verifyResult.error || 'Verification failed');
        setAttemptsRemaining(verifyResult.attempts_remaining || null);
        setIsVerifying(false);
        return;
      }

      // If verification successful, complete the login
      const loginResult = await twoFactorApi.complete2FALogin(
        tempToken,
        tempAccessToken,
        tempRefreshToken,
        verifyResult.device_token
      );

      // Store device token if user chose to trust this device
      // Use loginResult.username to ensure consistency with stored username
      if (trustDevice && verifyResult.device_token) {
        console.log('Storing device token for user:', loginResult.username);
        storeDeviceToken(loginResult.username, verifyResult.device_token);
      }

      onLoginSuccess(loginResult.id_token, loginResult.username);
    } catch (err) {
      const error = err as any;
      setTwoFactorError(error.message || 'Verification failed');
      setAttemptsRemaining(error.attempts_remaining || null);
    } finally {
      setIsVerifying(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!loginData.username) {
      setError('Please enter your username first');
      return;
    }

    setError('');
    setShowForgotPassword(true);

    try {
      await authApi.forgotPassword(loginData.username);
      setResetSuccess(true);
    } catch (err) {
      setError('Failed to send verification code. Please try again');
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!loginData.username || !verificationCode || !newPassword) {
      setError('Please fill in all fields');
      return;
    }

    setResetLoading(true);

    try {
      await authApi.confirmNewPassword(loginData.username, verificationCode, newPassword);
      setError('');
      setShowForgotPassword(false);
      setResetSuccess(false);
      setVerificationCode('');
      setNewPassword('');
      alert('Password reset successful! Please log in with your new password.');
    } catch (err) {
      const error = err as ApiError;
      setError(error.message || 'Failed to reset password');
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <div className="w-full max-w-md mx-auto animate-slide-up">
      <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl shadow-2xl p-6 hover:shadow-3xl hover:border-white/30 transition-all duration-300 relative overflow-hidden group">
        {/* Animated border gradient */}
        <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 via-purple-500/20 to-pink-500/20 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 -z-10"></div>

        {/* Shimmer effect */}
        <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000 bg-gradient-to-r from-transparent via-white/5 to-transparent"></div>

        <div className="text-center mb-4 relative z-10">
          <h2 className="text-3xl font-bold text-white mb-1 bg-gradient-to-r from-white to-blue-200 bg-clip-text text-transparent">
            Welcome Back
          </h2>
          <p className="text-white/80 text-sm">Sign in to continue to Patent Loop</p>
        </div>

        {error && (
          <div className="mb-3 p-2.5 bg-red-500/20 border border-red-500/50 rounded-lg flex items-center gap-2 text-white text-sm animate-shake backdrop-blur-sm font-medium">
            <AlertCircle className="w-4 h-4 flex-shrink-0 animate-pulse" />
            <span>{error}</span>
          </div>
        )}

        {requires2FA ? (
          <>
            <div className="mb-4 p-3 bg-blue-500/20 border border-blue-500/50 rounded-lg text-white text-sm backdrop-blur-sm">
              <p className="font-semibold">Two-Factor Authentication Required</p>
              <p className="mt-1">A verification code has been sent to your email.</p>
            </div>

            {twoFactorError && (
              <div className="mb-3 p-2.5 bg-red-500/20 border border-red-500/50 rounded-lg flex items-center gap-2 text-white text-sm animate-shake backdrop-blur-sm font-medium">
                <AlertCircle className="w-4 h-4 flex-shrink-0 animate-pulse" />
                <div>
                  <span>{twoFactorError}</span>
                  {attemptsRemaining !== null && (
                    <span className="block text-xs mt-1">
                      {attemptsRemaining} {attemptsRemaining === 1 ? 'attempt' : 'attempts'} remaining
                    </span>
                  )}
                </div>
              </div>
            )}

            <form onSubmit={handle2FAVerification} className="space-y-3 relative z-10">
              <div>
                <Input
                  type="text"
                  placeholder="Enter 6-digit code"
                  value={twoFactorCode}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                    setTwoFactorCode(value);
                    setTwoFactorError('');
                  }}
                  className="bg-white/90 border-white/30 text-gray-900 placeholder:text-gray-500 focus:bg-white text-center text-2xl tracking-widest font-mono"
                  maxLength={6}
                  autoFocus
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="trustDevice"
                  checked={trustDevice}
                  onChange={(e) => setTrustDevice(e.target.checked)}
                  className="w-4 h-4 rounded border-white/30 bg-white/90 text-blue-600 focus:ring-2 focus:ring-blue-400"
                />
                <label htmlFor="trustDevice" className="text-white/90 text-sm cursor-pointer">
                  Trust this device for 30 days
                </label>
              </div>

              <Button
                type="submit"
                disabled={isVerifying || twoFactorCode.length !== 6}
                className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold py-5 rounded-xl shadow-lg hover:shadow-2xl transform hover:scale-[1.02] transition-all duration-300"
              >
                {isVerifying ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin inline" />
                    Verifying...
                  </>
                ) : (
                  'Verify Code'
                )}
              </Button>

              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  setRequires2FA(false);
                  setTwoFactorCode('');
                  setTwoFactorError('');
                  setTrustDevice(false);
                  setAttemptsRemaining(null);
                }}
                className="w-full text-white/90 hover:text-white hover:bg-white/10"
              >
                Back to Login
              </Button>
            </form>
          </>
        ) : (
          <form onSubmit={showForgotPassword ? handleResetPassword : handleLogin} className="space-y-3 relative z-10">
            <div className="transform transition-all duration-300 hover:scale-[1.02]">
              <Input
                type="text"
                name="username"
                placeholder="Username"
                value={loginData.username}
                onChange={handleChange}
                className="bg-white/90 border-white/30 text-gray-900 placeholder:text-gray-500 focus:bg-white focus:border-blue-400 focus:ring-2 focus:ring-blue-400/50 transition-all duration-200"
              />
            </div>

            {!showForgotPassword ? (
              <div className="transform transition-all duration-300 hover:scale-[1.02]">
                <Input
                  type="password"
                  name="password"
                  placeholder="Password"
                  value={loginData.password}
                  onChange={handleChange}
                  className="bg-white/90 border-white/30 text-gray-900 placeholder:text-gray-500 focus:bg-white focus:border-blue-400 focus:ring-2 focus:ring-blue-400/50 transition-all duration-200"
                />
              </div>
            ) : (
            <>
              <div className="transform transition-all duration-300 hover:scale-[1.02]">
                <Input
                  type="text"
                  placeholder="Verification Code"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value)}
                  className="bg-white/90 border-white/30 text-gray-900 placeholder:text-gray-500 focus:bg-white focus:border-blue-400 focus:ring-2 focus:ring-blue-400/50 transition-all duration-200"
                />
              </div>
              <div className="transform transition-all duration-300 hover:scale-[1.02]">
                <Input
                  type="password"
                  placeholder="New Password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="bg-white/90 border-white/30 text-gray-900 placeholder:text-gray-500 focus:bg-white focus:border-blue-400 focus:ring-2 focus:ring-blue-400/50 transition-all duration-200"
                />
              </div>
              {resetSuccess && (
                <p className="text-green-300 text-sm">
                  Verification code sent to your email
                </p>
              )}
            </>
          )}

          <Button
            type="submit"
            disabled={isLoading || resetLoading}
            className="w-full bg-gradient-to-r from-blue-600 via-blue-700 to-purple-600 hover:from-blue-700 hover:via-purple-700 hover:to-pink-600 text-white font-semibold py-5 rounded-xl shadow-lg hover:shadow-2xl transform hover:scale-[1.02] transition-all duration-300 relative overflow-hidden group"
          >
            <span className="relative z-10">
              {(isLoading || resetLoading) ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin inline" />
                  {showForgotPassword ? 'Resetting...' : 'Logging in...'}
                </>
              ) : (
                <>{showForgotPassword ? 'Reset Password' : 'Login'}</>
              )}
            </span>
            <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700"></div>
          </Button>

          {!showForgotPassword && (
            <div className="space-y-1 -mt-1">
              <Button
                type="button"
                variant="ghost"
                onClick={handleForgotPassword}
                className="w-full text-sm text-white/90 hover:text-white hover:bg-white/10 transform hover:scale-[1.02] transition-all duration-200"
              >
                Forgot password?
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={onSwitchToConfirm}
                className="w-full text-sm text-white/90 hover:text-white hover:bg-white/10 transform hover:scale-[1.02] transition-all duration-200"
              >
                Enter confirmation code
              </Button>
            </div>
          )}

          {showForgotPassword && (
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setShowForgotPassword(false);
                setResetSuccess(false);
                setError('');
              }}
              className="w-full text-white/90 hover:text-white hover:bg-white/10 transform hover:scale-[1.02] transition-all duration-200"
            >
              Back to Login
            </Button>
          )}
        </form>
        )}

        {!requires2FA && (
        <div className="mt-4 flex flex-col gap-2 relative z-10">
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/30 to-blue-500/30 rounded-xl blur-sm"></div>
            <button
              onClick={onSwitchToSignup}
              className="relative w-full bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white font-bold py-3 px-6 rounded-xl shadow-lg hover:shadow-2xl transform hover:scale-[1.02] transition-all duration-300 flex items-center justify-center gap-2 group"
            >
              <span>Create Free Account</span>
              <span className="inline-block transform group-hover:translate-x-1 transition-transform duration-200">→</span>
            </button>
          </div>
          <p className="text-white/60 text-xs text-center">
            50 Free Credits • No Credit Card Required
          </p>
        </div>
        )}
      </div>
    </div>
  );
};
